import * as THREE from "three";
import { gerstnerDisplacement, gerstnerHeight } from "./ocean";
import { makeTransomName, type ClubMaterials } from "./materials";
import { PALETTE } from "./config";

export type BoatKind = "motor" | "express" | "sloop" | "daysailer" | "catamaran" | "runabout";

export type YachtSpec = {
  name: string;
  kind: BoatKind;
  length: number;
  beam: number;
  draft: number;
  flybridge?: boolean;
  accent: number;
  sailsUp?: boolean;
};

export type Berth = {
  id: string;
  x: number;
  z: number;
  heading: number;
  pierSide: -1 | 1 | 0;
};

const _p = new THREE.Vector3();
const _d = new THREE.Vector3();

type HullStyle = "motor" | "sail" | "runabout";

/** Metres of deck above the waterline at amidships. */
export function hullFreeboard(style: HullStyle, length: number): number {
  if (style === "sail") return THREE.MathUtils.clamp(length * 0.075, 0.9, 1.3);
  if (style === "runabout") return THREE.MathUtils.clamp(length * 0.08, 0.58, 0.88);
  return THREE.MathUtils.clamp(length * 0.085, 1.12, 1.6);
}

function hullGeometry(length: number, beam: number, draft: number, style: HullStyle): THREE.BufferGeometry {
  const freeboard = hullFreeboard(style, length);
  const stations =
    style === "sail"
      ? [
          { t: 0, w: 0.02, deck: 1.55, keel: 0.08 },
          { t: 0.06, w: 0.28, deck: 1.38, keel: 0.35 },
          { t: 0.14, w: 0.58, deck: 1.18, keel: 0.72 },
          { t: 0.28, w: 0.88, deck: 1.06, keel: 1 },
          { t: 0.46, w: 1, deck: 1, keel: 1 },
          { t: 0.64, w: 0.96, deck: 1.02, keel: 0.92 },
          { t: 0.8, w: 0.78, deck: 1.08, keel: 0.7 },
          { t: 0.92, w: 0.55, deck: 1.12, keel: 0.38 },
          { t: 1, w: 0.42, deck: 1.16, keel: 0.12 },
        ]
      : style === "runabout"
        ? [
            { t: 0, w: 0.08, deck: 1.22, keel: 0.12 },
            { t: 0.08, w: 0.42, deck: 1.12, keel: 0.4 },
            { t: 0.2, w: 0.82, deck: 1.04, keel: 0.75 },
            { t: 0.4, w: 1, deck: 1, keel: 1 },
            { t: 0.62, w: 1, deck: 1, keel: 1 },
            { t: 0.82, w: 0.94, deck: 1.04, keel: 0.85 },
            { t: 1, w: 0.88, deck: 1.08, keel: 0.45 },
          ]
        : [
            { t: 0, w: 0.04, deck: 1.32, keel: 0.08 },
            { t: 0.06, w: 0.32, deck: 1.2, keel: 0.28 },
            { t: 0.14, w: 0.68, deck: 1.1, keel: 0.62 },
            { t: 0.28, w: 0.94, deck: 1.04, keel: 0.92 },
            { t: 0.48, w: 1, deck: 1, keel: 1 },
            { t: 0.68, w: 0.98, deck: 1.02, keel: 0.96 },
            { t: 0.86, w: 0.9, deck: 1.06, keel: 0.72 },
            { t: 1, w: 0.82, deck: 1.1, keel: 0.38 },
          ];

  const profile =
    style === "sail"
      ? [
          { u: -1, v: 1 },
          { u: -0.98, v: 0.4 },
          { u: -0.82, v: 0.05 },
          { u: -0.42, v: -0.45 },
          { u: 0, v: -1 },
          { u: 0.42, v: -0.45 },
          { u: 0.82, v: 0.05 },
          { u: 0.98, v: 0.4 },
          { u: 1, v: 1 },
        ]
      : [
          { u: -1, v: 1 },
          { u: -1, v: 0.42 },
          { u: -0.94, v: 0.06 },
          { u: -0.72, v: -0.28 },
          { u: -0.28, v: -0.78 },
          { u: 0, v: -1 },
          { u: 0.28, v: -0.78 },
          { u: 0.72, v: -0.28 },
          { u: 0.94, v: 0.06 },
          { u: 1, v: 0.42 },
          { u: 1, v: 1 },
        ];

  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const white = new THREE.Color(PALETTE.yachtWhite);
  const anti = new THREE.Color(PALETTE.antifoul);
  const navy = new THREE.Color(PALETTE.navy);
  const cols = stations.length;
  const rows = profile.length;

  for (let i = 0; i < cols; i++) {
    const st = stations[i];
    const z = length * 0.5 - st.t * length;
    const half = beam * 0.5 * st.w;
    const deckY = freeboard * st.deck;
    const keelY = -draft * st.keel;
    for (let j = 0; j < rows; j++) {
      const pr = profile[j];
      const x = pr.u * half;
      const y = pr.v >= 0 ? pr.v * deckY : -pr.v * keelY;
      positions.push(x, y, z);
      let col = white;
      if (y < -0.08) col = anti;
      else if (y < 0.14) col = navy;
      colors.push(col.r, col.g, col.b);
    }
  }

  for (let i = 0; i < cols - 1; i++) {
    for (let j = 0; j < rows - 1; j++) {
      const a = i * rows + j;
      const b = a + 1;
      const c = (i + 1) * rows + j;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const deckStart = positions.length / 3;
  for (let i = 0; i < cols; i++) {
    const st = stations[i];
    const z = length * 0.5 - st.t * length;
    const half = beam * 0.5 * st.w;
    const deckY = freeboard * st.deck;
    positions.push(-half, deckY, z, half, deckY, z);
    colors.push(white.r, white.g, white.b, white.r, white.g, white.b);
  }
  for (let i = 0; i < cols - 1; i++) {
    const a = deckStart + i * 2;
    indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

function hullMesh(length: number, beam: number, draft: number, style: HullStyle, mats: ClubMaterials): THREE.Mesh {
  const hullMat = mats.yachtWhite.clone();
  hullMat.vertexColors = true;
  const hull = new THREE.Mesh(hullGeometry(length, beam, draft, style), hullMat);
  hull.castShadow = true;
  hull.receiveShadow = true;
  return hull;
}

function nameplate(name: string, length: number): THREE.Mesh {
  const plate = new THREE.Mesh(
    new THREE.PlaneGeometry(Math.min(4.2, length * 0.28), 0.38),
    new THREE.MeshBasicMaterial({ map: makeTransomName(name), transparent: true }),
  );
  plate.position.set(0, 0.95, -length * 0.5 - 0.03);
  plate.rotation.y = Math.PI;
  return plate;
}

function addRails(g: THREE.Group, length: number, beam: number, deckY: number, mat: THREE.Material): void {
  const railH = 0.5;
  for (const side of [-1, 1]) {
    for (let i = 0; i < 6; i++) {
      const z = THREE.MathUtils.lerp(length * 0.36, -length * 0.4, i / 5);
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, railH, 6), mat);
      post.position.set(side * beam * 0.46, deckY + railH * 0.5, z);
      g.add(post);
    }
  }
}

function warpCabin(w: number, h: number, l: number): THREE.BoxGeometry {
  const geo = new THREE.BoxGeometry(w, h, l, 1, 1, 6);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const z = pos.getZ(i);
    const t = THREE.MathUtils.clamp((z + l * 0.5) / l, 0, 1);
    pos.setX(i, pos.getX(i) * (0.72 + 0.28 * (1 - t * 0.7)));
    if (t > 0.65) pos.setY(i, pos.getY(i) + (t - 0.65) * 0.25);
  }
  geo.computeVertexNormals();
  return geo;
}

function buildMotor(spec: YachtSpec, mats: ClubMaterials, express: boolean): THREE.Group {
  const g = new THREE.Group();
  g.add(hullMesh(spec.length, spec.beam, spec.draft, "motor", mats));
  const deckY = hullFreeboard("motor", spec.length);
  const cabinLen = spec.length * (express ? 0.42 : 0.5);
  const cabinW = spec.beam * 0.76;
  const cabinH = express ? 1.35 : spec.flybridge ? 1.28 : 1.5;
  const cabinZ = express ? -spec.length * 0.08 : -spec.length * 0.02;
  const cabin = new THREE.Mesh(warpCabin(cabinW, cabinH, cabinLen), mats.yachtWhite);
  cabin.position.set(0, deckY + cabinH * 0.5, cabinZ);
  cabin.castShadow = true;
  g.add(cabin);
  const glass = new THREE.Mesh(
    new THREE.BoxGeometry(cabinW * 0.92, cabinH * 0.38, cabinLen * 0.7),
    mats.darkGlass,
  );
  glass.position.set(0, deckY + cabinH * 0.62, cabinZ + cabinLen * 0.08);
  g.add(glass);
  const screen = new THREE.Mesh(new THREE.BoxGeometry(cabinW * 0.88, cabinH * 0.55, 0.07), mats.darkGlass);
  screen.position.set(0, deckY + cabinH * 0.68, cabinZ + cabinLen * 0.48);
  screen.rotation.x = -0.28;
  g.add(screen);

  if (spec.flybridge && !express) {
    const fly = new THREE.Mesh(new THREE.BoxGeometry(cabinW * 0.62, 0.14, cabinLen * 0.4), mats.yachtWhite);
    fly.position.set(0, deckY + cabinH + 0.18, cabinZ - cabinLen * 0.02);
    fly.castShadow = true;
    g.add(fly);
    const helm = new THREE.Mesh(new THREE.BoxGeometry(cabinW * 0.36, 0.48, 0.28), mats.navy);
    helm.position.set(0, deckY + cabinH + 0.5, cabinZ + cabinLen * 0.1);
    g.add(helm);
    const arch = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.03, 8, 16, Math.PI), mats.chrome);
    arch.rotation.z = Math.PI / 2;
    arch.position.set(0, deckY + cabinH + 1.15, cabinZ);
    g.add(arch);
  }

  const cockpit = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.68, 0.05, spec.length * 0.18), mats.teak);
  cockpit.position.set(0, deckY + 0.02, -spec.length * 0.34);
  g.add(cockpit);
  const platform = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.58, 0.07, spec.length * 0.07), mats.yachtWhite);
  platform.position.set(0, 0.08, -spec.length * 0.53);
  g.add(platform);
  addRails(g, spec.length, spec.beam, deckY, mats.chrome);
  g.add(nameplate(spec.name, spec.length));
  return g;
}

function buildRunabout(spec: YachtSpec, mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  g.add(hullMesh(spec.length, spec.beam, spec.draft, "runabout", mats));
  const deckY = hullFreeboard("runabout", spec.length);
  const wind = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.7, 0.55, 0.06), mats.darkGlass);
  wind.position.set(0, deckY + 0.42, spec.length * 0.08);
  wind.rotation.x = -0.35;
  g.add(wind);
  const dash = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.55, 0.22, 0.35), mats.navy);
  dash.position.set(0, deckY + 0.16, spec.length * 0.02);
  g.add(dash);
  const seats = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.5, 0.16, spec.length * 0.22), mats.cushion);
  seats.position.set(0, deckY + 0.08, -spec.length * 0.12);
  g.add(seats);
  const engine = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.7, 0.28), mats.steel);
  engine.position.set(0, deckY - 0.15, -spec.length * 0.48);
  g.add(engine);
  g.add(nameplate(spec.name, spec.length));
  return g;
}

function sailCloth(color = 0xf3eee4): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.85,
    metalness: 0,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.88,
  });
}

function addRig(
  g: THREE.Group,
  spec: YachtSpec,
  mats: ClubMaterials,
  mastZ: number,
  mastH: number,
  sailsUp: boolean,
): void {
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, mastH, 8), mats.chrome);
  mast.position.set(0, mastH * 0.5, mastZ);
  mast.castShadow = true;
  g.add(mast);
  const boomLen = spec.length * 0.4;
  const boomH = Math.max(1.35, hullFreeboard("sail", spec.length) + 0.25);
  const boom = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, boomLen, 6), mats.teak);
  boom.rotation.x = Math.PI / 2;
  boom.position.set(0.04, boomH, mastZ - boomLen * 0.45);
  g.add(boom);

  const stay = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, spec.length * 0.62, 4), mats.steel);
  stay.position.set(0, mastH * 0.48, mastZ + spec.length * 0.22);
  stay.rotation.x = 0.72;
  g.add(stay);
  const back = stay.clone();
  back.position.set(0, mastH * 0.45, mastZ - spec.length * 0.28);
  back.rotation.x = -0.55;
  g.add(back);

  if (sailsUp) {
    const main = new THREE.BufferGeometry();
    const head = new THREE.Vector3(0.06, mastH * 0.92, mastZ);
    const tack = new THREE.Vector3(0.06, boomH, mastZ - 0.05);
    const clew = new THREE.Vector3(0.06, boomH + 0.05, mastZ - boomLen * 0.9);
    main.setFromPoints([head, tack, clew]);
    main.setIndex([0, 1, 2]);
    main.computeVertexNormals();
    const mainMesh = new THREE.Mesh(main, sailCloth());
    mainMesh.castShadow = true;
    g.add(mainMesh);

    const jib = new THREE.BufferGeometry();
    const jHead = new THREE.Vector3(0.02, mastH * 0.78, mastZ);
    const jTack = new THREE.Vector3(0.02, hullFreeboard("sail", spec.length) * 0.85, spec.length * 0.46);
    const jClew = new THREE.Vector3(0.55, 1.0, mastZ + spec.length * 0.08);
    jib.setFromPoints([jHead, jTack, jClew]);
    jib.setIndex([0, 1, 2]);
    jib.computeVertexNormals();
    g.add(new THREE.Mesh(jib, sailCloth(0xf7f4ea)));
  } else {
    const cover = new THREE.Mesh(new THREE.CapsuleGeometry(0.16, boomLen * 0.75, 4, 8), mats.navy);
    cover.rotation.x = Math.PI / 2;
    cover.position.set(0.05, boomH + 0.12, mastZ - boomLen * 0.4);
    g.add(cover);
  }
}

function addKeel(g: THREE.Group, spec: YachtSpec, mats: ClubMaterials): void {
  const fin = new THREE.Mesh(new THREE.BoxGeometry(0.12, spec.draft * 1.15, spec.length * 0.18), mats.steel);
  fin.position.set(0, -spec.draft * 0.85, spec.length * 0.02);
  g.add(fin);
  const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), mats.steel);
  bulb.scale.set(0.7, 0.7, 1.4);
  bulb.position.set(0, -spec.draft * 1.35, spec.length * 0.02);
  g.add(bulb);
  const rudder = new THREE.Mesh(new THREE.BoxGeometry(0.06, spec.draft * 0.7, 0.35), mats.steel);
  rudder.position.set(0, -spec.draft * 0.45, -spec.length * 0.42);
  g.add(rudder);
}

function buildSloop(spec: YachtSpec, mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  g.add(hullMesh(spec.length, spec.beam, spec.draft, "sail", mats));
  const deckY = hullFreeboard("sail", spec.length);
  const cabinLen = spec.length * 0.32;
  const cabin = new THREE.Mesh(warpCabin(spec.beam * 0.62, 0.95, cabinLen), mats.yachtWhite);
  cabin.position.set(0, deckY + 0.5, spec.length * 0.02);
  cabin.castShadow = true;
  g.add(cabin);
  const hatch = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.42, 0.08, spec.length * 0.12), mats.darkGlass);
  hatch.position.set(0, deckY + 0.92, spec.length * 0.04);
  g.add(hatch);
  const cockpit = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.5, 0.06, spec.length * 0.16), mats.teak);
  cockpit.position.set(0, deckY, -spec.length * 0.22);
  g.add(cockpit);
  addKeel(g, spec, mats);
  addRig(g, spec, mats, spec.length * 0.12, spec.length * 1.22, spec.sailsUp !== false);
  addRails(g, spec.length * 0.7, spec.beam, deckY, mats.chrome);
  g.add(nameplate(spec.name, spec.length));
  return g;
}

function buildDaysailer(spec: YachtSpec, mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  g.add(hullMesh(spec.length, spec.beam, spec.draft, "sail", mats));
  const deckY = hullFreeboard("sail", spec.length);
  const cockpit = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.62, 0.08, spec.length * 0.35), mats.teak);
  cockpit.position.set(0, deckY, -spec.length * 0.08);
  g.add(cockpit);
  addKeel(g, spec, mats);
  addRig(g, spec, mats, spec.length * 0.1, spec.length * 1.15, true);
  g.add(nameplate(spec.name, spec.length));
  return g;
}

function buildCatamaran(spec: YachtSpec, mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  const hullW = spec.beam * 0.22;
  const gap = spec.beam * 0.38;
  for (const side of [-1, 1]) {
    const hull = hullMesh(spec.length, hullW, spec.draft, "sail", mats);
    hull.position.x = side * gap;
    g.add(hull);
  }
  const deckY = hullFreeboard("sail", spec.length);
  const tramp = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.72, 0.05, spec.length * 0.55), mats.navy);
  tramp.position.set(0, deckY, spec.length * 0.05);
  g.add(tramp);
  const cabin = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.42, 1.05, spec.length * 0.28), mats.yachtWhite);
  cabin.position.set(0, deckY + 0.55, -spec.length * 0.02);
  cabin.castShadow = true;
  g.add(cabin);
  const glass = new THREE.Mesh(new THREE.BoxGeometry(spec.beam * 0.38, 0.45, spec.length * 0.18), mats.darkGlass);
  glass.position.set(0, deckY + 0.72, spec.length * 0.06);
  g.add(glass);
  addRig(g, spec, mats, spec.length * 0.08, spec.length * 1.35, spec.sailsUp !== false);
  g.add(nameplate(spec.name, spec.length));
  return g;
}

export function buildYacht(spec: YachtSpec, mats: ClubMaterials): THREE.Group {
  const g =
    spec.kind === "sloop"
      ? buildSloop(spec, mats)
      : spec.kind === "daysailer"
        ? buildDaysailer(spec, mats)
        : spec.kind === "catamaran"
          ? buildCatamaran(spec, mats)
          : spec.kind === "runabout"
            ? buildRunabout(spec, mats)
            : buildMotor(spec, mats, spec.kind === "express");
  g.name = spec.name;
  return g;
}

type BoatState = {
  heave: number;
  pitch: number;
  roll: number;
  yaw: number;
  surge: number;
  sway: number;
  heaveV: number;
  pitchV: number;
  rollV: number;
  yawV: number;
  surgeV: number;
  swayV: number;
};

export class DockedYacht {
  readonly group: THREE.Group;
  readonly spec: YachtSpec;
  readonly berth: Berth;
  private state: BoatState = {
    heave: 0,
    pitch: 0,
    roll: 0,
    yaw: 0,
    surge: 0,
    sway: 0,
    heaveV: 0,
    pitchV: 0,
    rollV: 0,
    yawV: 0,
    surgeV: 0,
    swayV: 0,
  };

  constructor(group: THREE.Group, spec: YachtSpec, berth: Berth) {
    this.group = group;
    this.spec = spec;
    this.berth = berth;
    group.position.set(berth.x, 0, berth.z);
    group.rotation.y = berth.heading;
  }

  update(time: number, dt: number): void {
    const { length, beam } = this.spec;
    const heading = this.berth.heading;
    const cos = Math.cos(heading);
    const sin = Math.sin(heading);

    const sample = (localX: number, localZ: number) => {
      const wx = this.berth.x + localX * cos + localZ * sin;
      const wz = this.berth.z - localX * sin + localZ * cos;
      return gerstnerHeight(wx, wz, time);
    };

    const bow = sample(0, length * 0.38);
    const stern = sample(0, -length * 0.38);
    const port = sample(-beam * 0.35, 0);
    const starboard = sample(beam * 0.35, 0);
    const mid = sample(0, 0);

    const targetHeave = (bow + stern + port + starboard + mid) / 5;
    const targetPitch = Math.atan2(bow - stern, length * 0.76);
    const targetRoll = Math.atan2(starboard - port, beam * 0.7);

    gerstnerDisplacement(this.berth.x, this.berth.z, time, undefined, _d);
    const targetSurge = _d.z * 0.22;
    const targetSway = _d.x * 0.18;
    const targetYaw =
      Math.atan2(sample(beam * 0.2, length * 0.3) - sample(-beam * 0.2, length * 0.3), beam * 0.5) * 0.15;

    const spring = (value: number, vel: number, target: number, k: number, damp: number) => {
      const a = (target - value) * k - vel * damp;
      const v = vel + a * dt;
      return { value: value + v * dt, vel: v };
    };

    const s = this.state;
    ({ value: s.heave, vel: s.heaveV } = spring(s.heave, s.heaveV, targetHeave, 14, 5.5));
    ({ value: s.pitch, vel: s.pitchV } = spring(s.pitch, s.pitchV, targetPitch, 10, 4.8));
    ({ value: s.roll, vel: s.rollV } = spring(s.roll, s.rollV, targetRoll, 12, 5.2));
    ({ value: s.surge, vel: s.surgeV } = spring(s.surge, s.surgeV, targetSurge, 6, 3.4));
    ({ value: s.sway, vel: s.swayV } = spring(s.sway, s.swayV, targetSway, 8, 4.1));
    ({ value: s.yaw, vel: s.yawV } = spring(s.yaw, s.yawV, targetYaw, 5, 3.2));

    s.surge = THREE.MathUtils.clamp(s.surge, -0.55, 0.55);
    s.sway = THREE.MathUtils.clamp(s.sway, -0.35, 0.35);
    s.yaw = THREE.MathUtils.clamp(s.yaw, -0.05, 0.05);

    _p.set(this.berth.x + s.sway, s.heave + 0.08, this.berth.z + s.surge);
    this.group.position.copy(_p);
    this.group.rotation.set(s.pitch, heading + s.yaw, s.roll);
  }
}

export const FLEET: YachtSpec[] = [
  { name: "AURORA", kind: "motor", length: 17.2, beam: 5.1, draft: 1.3, flybridge: true, accent: 0xe85a24 },
  { name: "NEREID", kind: "express", length: 14.8, beam: 4.4, draft: 1.1, accent: 0x1b365d },
  { name: "PIPER", kind: "daysailer", length: 8.8, beam: 2.9, draft: 1.2, accent: 0xe85a24, sailsUp: true },
  { name: "ZEPHYR", kind: "sloop", length: 14.2, beam: 4.1, draft: 1.85, accent: 0x1aa6a1, sailsUp: true },
  { name: "ALTAIR", kind: "sloop", length: 14.2, beam: 4.1, draft: 1.85, accent: 0x1b365d, sailsUp: true },
  { name: "TWIN", kind: "catamaran", length: 12.4, beam: 6.6, draft: 1.05, accent: 0x4ecdc4, sailsUp: true },
  { name: "COMO", kind: "runabout", length: 8.4, beam: 2.8, draft: 0.55, accent: 0xe85a24 },
  { name: "SIRIUS", kind: "sloop", length: 11.6, beam: 3.6, draft: 1.55, accent: 0x1b365d, sailsUp: false },
];

export const BERTHS: Berth[] = [
  { id: "W1", x: -30, z: 20, heading: 0.25, pierSide: -1 },
  { id: "W2", x: -32, z: 2, heading: 0.1, pierSide: -1 },
  { id: "W3", x: -24, z: -10, heading: -0.12, pierSide: -1 },
  { id: "M1", x: 4, z: 10, heading: 0.45, pierSide: 0 },
  { id: "E1", x: 30, z: 16, heading: -0.18, pierSide: 1 },
  { id: "E2", x: 28, z: -4, heading: -0.22, pierSide: 1 },
  { id: "E3", x: 26, z: -16, heading: 0.06, pierSide: 1 },
  { id: "M2", x: 12, z: 28, heading: 0.2, pierSide: 0 },
];
